import { FunctionComponent, useState, useEffect, ReactNode } from 'react';
import styled, { DefaultTheme, useTheme } from 'styled-components';
import { styles } from '../styles/themes/style';
import DropdownMenu from '../components/dropdown/DropdownMenu';
import DropdownItem from '../components/dropdown/DropdownItem';
import TabsSearch from '../components/TabsSearch';
import { ITabProperties } from '../components/TabsView';
import Row from '../components/Row';
import Checkbox from '../components/Checkbox';
import SvgFragment from './SvgFragment';
import ActionText from '../components/ActionText';
import { AppRoutes } from '../interfaces/store/appStore';

export interface IAdvancedFilterOption {
    value: string,
    label: ReactNode,
    id: string,
    selected: boolean,
    object: Object
}

interface IAdvancedFilterFragmentProps {
    customSingleValue?: any
    upgradePlan?: boolean
    drop?: string
    filterOptions: IAdvancedFilterOption[];
    selectedItems: any[];
    infoTitle: string;
    inputTitle: string;
    tabs: ITabProperties[];
    showSearchField?: boolean;
    inputSelect?: boolean;
    resetDisabled?: boolean;
    styledRow?: boolean;
    disabled?: boolean;
    tooltipMessage?: string;
    icon?: ReactNode;
    onClickedItemHandler?: (item: any) => void;
    onSelectedItemHandler: (items: any[], item?: any) => void;
    onResetSelectionsHandler: () => void;
    hideResetButton?: boolean;
    panelWidth?: string;
    onSelectedTabHandler?: (tab: any) => void;
    onRemovedItemHandler?: (items: any[], item?: any) => void;
}

const AdvancedFilterFragment: FunctionComponent<IAdvancedFilterFragmentProps> = ({ upgradePlan, customSingleValue, drop, filterOptions, selectedItems, infoTitle, inputTitle, tabs, showSearchField = true, inputSelect = false, styledRow, disabled = false, tooltipMessage, icon, onClickedItemHandler, onSelectedItemHandler, onResetSelectionsHandler, onSelectedTabHandler, onRemovedItemHandler, hideResetButton = false, panelWidth }) => {
 
    const [selectOptions, setSelectOptions] = useState<IAdvancedFilterOption[]>([]);
    const [searchByNameValue, setSearchByNameValue] = useState('');
    const theme: DefaultTheme = useTheme();
    //todo: adjust other styles to the new design and use Text for all the labels
    const dropdownButton = 
            <StyledControlDiv
                upgradePlan={!!upgradePlan}
                > 
                {icon && <IconSlot>{icon}</IconSlot>}
                {customSingleValue ? customSingleValue :
                    <><LabelText>{inputTitle}:</LabelText>
                    {
                        (selectedItems.length) ?
                            <ValueText $active>
                                {selectedItems.length ?? 0}&nbsp;Selected
                            </ValueText>
                            :
                            <ValueText>
                                {upgradePlan ? (
                                    <StyledActionText
                                    icon="lock2"
                                    onClickHandler={(e: any) => {
                                        e.stopPropagation();
                                        window.open(`${window.location.origin}${AppRoutes.BILLING}`, '_blank');
                                    }}
                                    >
                                    {'Upgrade'}
                                    </StyledActionText>
                                ) : (
                                    'Show All'
                                )}
                            </ValueText>
                    }</>
            }
            </StyledControlDiv>

    //set prefilled values
    useEffect(() => {
        const filtered = filterOptions
            ?.filter(option => typeof option?.value === 'string' && option.value.toLowerCase().includes(searchByNameValue?.toLowerCase()))
            .map(option => ({
                ...option,
                selected: selectedItems?.some(item => item._id === option.id)
            }));

        // keep selected items that are not in the filtered list
        const selectedNotInFiltered = filterOptions
            ?.filter(option => selectedItems?.some(item => item._id === option.id) && !filtered.some(f => f.id === option.id))
            .map(option => ({
                ...option,
                selected: true
            }));

        setSelectOptions([...(filtered || []), ...(selectedNotInFiltered || [])]);
    }, [filterOptions, selectedItems, searchByNameValue]);
    
    const searchByNameHandler = (value: string) => {
        setSearchByNameValue(value);
        const filtered = filterOptions
            .filter(option => typeof option?.value === 'string' && option.value.toLowerCase().includes(value?.toLowerCase()))
            .map(option => ({
                ...option,
                selected: selectedItems?.some(item => item._id === option.id)
            }));

        // keep selected items that are not in the filtered list
        const selectedNotInFiltered = filterOptions
            .filter(option => selectedItems?.some(item => item._id === option.id) && !filtered.some(f => f.id === option.id))
            .map(option => ({
                ...option,
                selected: true
            }));

        setSelectOptions([...(filtered || []), ...(selectedNotInFiltered || [])]);
    };

    const toggleItemHandler = (event: any, item: IAdvancedFilterOption) => {
        event.stopPropagation()
        let add: boolean
        let selectedObjects: any[] = []
        setSelectOptions(selectOptions.map((option) => {
            if(option.id === item.id) {
                add = !option.selected
                let newOption = {...option, selected: add}
                if(newOption.selected) selectedObjects.push(newOption.object)
                return newOption
            }
            if(option.selected) selectedObjects.push(option.object)
            return {...option}
        }))
        onSelectedItemHandler(selectedObjects, item)
        onClickedItemHandler && onClickedItemHandler(item)
    }

    return (
        <Wrapper $panelWidth={panelWidth}>
            {upgradePlan ? <UpgradePlanWrapper alignItems>{dropdownButton}<SvgFragment type='dropdownArrow' /></UpgradePlanWrapper> :
            <DropdownMenu
                    title={dropdownButton}
                    tooltipMessage={tooltipMessage}
                    autoClose='outside'
                    drop={drop}
                    selectStyle
                    panelWidth={panelWidth}
                    disabled={disabled}
                >   
                    <TabsSearch 
                        inputSelect={inputSelect}
                        tabs={tabs}
                        infoTitle={infoTitle}
                        searchByNameValue={searchByNameValue}
                        selectedItems={selectedItems}
                        searchByNameHandler={showSearchField ? searchByNameHandler : undefined}
                        setSelectedTab={(e:any) => onSelectedTabHandler && onSelectedTabHandler(e)}
                        onRemovedItemHandler={onRemovedItemHandler && onRemovedItemHandler}
                        resetDisabled={!selectedItems.length}
                        resetSelectedValues={hideResetButton ? undefined : onResetSelectionsHandler}
                    >
                         <>
                         {selectOptions?.sort((a, b) => {
                            if(a.id === 'all') return -1
                            if(b.id === 'all') return 1
                            if(a.id === 'noTags') return -1
                            if(b.id === 'noTags') return 1 
                            return a.value < b.value ? -1 : a.value > b.value ? 1 : 0
                         }).map((item: IAdvancedFilterOption) => (
                            <div key={item.id}>
                              {styledRow ? (
                                  <DropdownItem $marginTop onClickHandler={(e: any) => toggleItemHandler(e, item)}>
                                      <StyledRow>
                                          <Checkbox $height={'24px'} checked={item.selected} checkboxId={item.id} />
                                          {item.label}
                                      </StyledRow>
                                  </DropdownItem>
                              ) : (
                                  <DropdownItem $color={theme.primaryColor} onClickHandler={(e: any) => toggleItemHandler(e, item)}>
                                      <Row alignItems gap="10px">
                                          <Checkbox checked={item.selected} checkboxId={item.id} />
                                          {item.label}
                                      </Row>
                                  </DropdownItem>
                              )}

                              {/* Spacing between "All members" and the rest of the list */}
                              {item.id === 'all' ? <div style={{ height: 15 }} /> : null}
                            </div>
                        ))}</>

                    </TabsSearch>
            </DropdownMenu>
        }
        </Wrapper>
    );
}

const Wrapper = styled.div<{ $panelWidth?: string }>`
    & .actions-dropdown + div {
        width: ${({ $panelWidth }) => ($panelWidth ? $panelWidth : '100%')};
        padding: 16px 15px;
        overflow-x: hidden;
    }
    & .dropdown-item {
        padding: 0;
        min-width: 0;
        overflow: hidden;
    }
    & .dropdown-item > div {
        min-width: 0;
        overflow: hidden;
    }
`
const StyledControlDiv = styled.div<{ upgradePlan: boolean }>`
    display: flex;
    justify-content: ${({ upgradePlan }) => (upgradePlan ? 'space-between' : 'flex-start')};
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
    height: 100%;
    padding-left: 0;
    box-sizing: border-box;
    border-radius: 8px;
    font-family: NunitoSans;
    font-size: 14px;
    line-height: 20px;
    cursor: pointer;
    position: relative;
    user-select: none;
    overflow: hidden;
`;
const IconSlot = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    color: ${styles.colors.black300};

    & svg {
        width: 16px;
        height: 16px;
    }
`
const LabelText = styled.span`
    flex-shrink: 0;
    font-weight: 500;
    color: ${styles.colors.black600};
`
const ValueText = styled.span<{ $active?: boolean }>`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
    color: ${({ $active }) => ($active ? styles.colors.primary600 : styles.colors.black300)};
    font-weight: ${({ $active }) => ($active ? 600 : 400)};
`
const StyledRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-width: 0;
    overflow: hidden;

    & > *:first-child {
        flex-shrink: 0;
    }
`
const UpgradePlanWrapper = styled(Row)`
    min-width: 296px;
    border: 2px solid ${styles.colors.black200};
    border-radius: 4px;
    background-color: ${styles.colors.black100};
`
const StyledActionText = styled(ActionText)`
    span {    
        color: ${styles.colors.black300};
        font-size: 12px;
    }

    &:hover{
        path { fill: ${styles.colors.primary400} }
    }

    div {
        margin-right: 4px;
        svg {
            margin-bottom: 4px;
        }
    }
`
export default AdvancedFilterFragment;
